import { Alert, Box, Button, IconButton, SxProps, Tooltip, Typography } from "@mui/material";
import { getContrastRatio } from "@mui/material/styles";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useTranslation } from "react-i18next";
import { ImageField, ImageInput, TextField, required, useRecordContext } from "react-admin";
import { ColorInput } from "react-admin-color-picker";
import { useWatch } from "react-hook-form";
import { ReactNode } from "react";
import Label from "../../../components/Label";
import Section from "../../../components/Common/Section";
import PaymentPagePreviewButton from "./PaymentPagePreviewButton";

export const PROJECT_TYPE_CHOICES = [
  { id: "cryptoPayment", name: "Crypto payment" },
  { id: "gasStation", name: "Gas station" },
];

// An entity type as read ("Crypto payment"); its code when unknown.
export const projectTypeLabel = (type?: string) =>
  PROJECT_TYPE_CHOICES.find((choice) => choice.id === type)?.name ?? type ?? "";

const SECTIONS = {
  info: { title: "Entity", subtitle: "Name, account, type and status" },
  paymentPage: { title: "Payment page", subtitle: "What customers see when they pay: name, contact details, logos and colors" },
  cryptoPayment: { title: "Payments", subtitle: "Conversion, fees, and how your systems are notified" },
  gasStation: { title: "Gas station", subtitle: "Currency, limits and where transfers can go" },
  coinhouse: { title: "Coinhouse", subtitle: "The Coinhouse user this entity is linked to" },
};

// A section of the entity screens, titled the same in the form and on the details page.
export const ProjectSection = ({ id, form, children }: { id: keyof typeof SECTIONS; form?: boolean; children: ReactNode }) => {
  const { t }: { t: any } = useTranslation();
  return (
    <Section form={form} title={t(SECTIONS[id].title)} subtitle={t(SECTIONS[id].subtitle)}>
      {children}
    </Section>
  );
};

// The entity list carries logos as storage keys; only fetching the entity itself returns
// them as signed URLs. The entity pages therefore refetch it instead of reusing the list's copy.
export const FETCH_ENTITY = { refetchOnMount: "always" } as const;

export const monoSx = { fontFamily: "monospace", fontSize: 13, fontWeight: 400 };

const COUNTER_URL =
  process.env.NODE_ENV === "production" ? "https://counter.coinhouse.com" : "https://counter.comptoirdubitcoin.com";

export const BooleanLabel = ({ value, labels = ["Yes", "No"] }: { value?: boolean; labels?: [string, string] }) => {
  const { t }: { t: any } = useTranslation();
  return <Label color={value ? "success" : "secondary"}>{t(value ? labels[0] : labels[1])}</Label>;
};

type LogoField = "logo" | "logoInverse";

// A logo as customers see it: the inverted logo sits on the entity's background colour.
export const LogoPreview = ({ field, sx }: { field: LogoField; sx?: SxProps }) => {
  const { t }: { t: any } = useTranslation();
  const { [field]: src, backgroundColor } = useRecordContext()?.parameters?.physicalPayment ?? {};
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        backgroundColor: field === "logoInverse" ? backgroundColor : "transparent",
        ...sx,
      }}
    >
      {src ? (
        <img src={src} alt={t("Logo preview")} style={{ maxWidth: "80%", maxHeight: "80%" }} />
      ) : (
        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          {t("No logo")}
        </Typography>
      )}
    </Box>
  );
};

// The current logo beside the file picker. The dataProvider uploads the picked file.
export const LogoUpload = ({ field, isRequired }: { field: LogoField; isRequired?: boolean }) => (
  <Box display="flex" flexDirection="column" gap={1.5}>
    <LogoPreview field={field} sx={{ height: 96 }} />
    <ImageInput label={false} source={field} accept="image/*" maxSize={2000000} validate={isRequired ? required() : undefined}>
      <ImageField source="src" title="title" />
    </ImageInput>
  </Box>
);

// A colour input with a swatch of the current value beside it.
export const SwatchColorInput = ({ source, label, isRequired }: { source: string; label: string; isRequired?: boolean }) => {
  const value = useWatch({ name: source });
  return (
    <Box display="flex" alignItems="flex-end" gap={1.5} sx={{ "& > div": { flex: 1 }, "& .MuiFormControl-root": { width: "100%" } }}>
      <ColorInput source={source} label={label} isRequired={isRequired} variant="filled" />
      <Box
        sx={{
          flex: "0 0 auto !important",
          width: 44,
          height: 44,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          backgroundColor: value || "transparent",
        }}
      />
    </Box>
  );
};

export const ColorValue = ({ source }: { source: string }) => {
  const value = useRecordContext()?.parameters?.physicalPayment?.[source];
  return (
    <Box display="inline-flex" alignItems="center" gap={1}>
      {value && <Box sx={{ width: 16, height: 16, borderRadius: 0.5, border: 1, borderColor: "divider", backgroundColor: value }} />}
      <TextField source={`parameters.physicalPayment.${source}`} sx={monoSx} />
    </Box>
  );
};

export const CopyButton = ({ value }: { value?: string }) => {
  const { t }: { t: any } = useTranslation();
  if (!value) return null;
  return (
    <Tooltip title={t("Copy")}>
      <IconButton size="small" aria-label={t("Copy")} onClick={() => navigator.clipboard.writeText(value)}>
        <ContentCopyOutlinedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

// A record value in monospace, with a button to copy it.
export const CopyableValue = ({ source }: { source: string }) => {
  const value: string | undefined = useRecordContext()?.[source];
  return (
    <Box display="inline-flex" alignItems="center" gap={0.5}>
      <TextField source={source} sx={monoSx} />
      <CopyButton value={value} />
    </Box>
  );
};

// The API key, shortened, with a button to copy it whole.
export const ApiKeyValue = () => {
  const apiKey: string | undefined = useRecordContext()?.apiKey;
  if (!apiKey) return <TextField source="apiKey" />;
  return (
    <Box display="inline-flex" alignItems="center" gap={0.5}>
      <Typography component="span" sx={monoSx}>
        {apiKey.slice(0, 10)}…{apiKey.slice(-10)}
      </Typography>
      <CopyButton value={apiKey} />
    </Box>
  );
};

// The linked Coinhouse user, with a link to its page in the Coinhouse Counter.
export const CoinhouseUserValue = () => {
  const { t }: { t: any } = useTranslation();
  const userId: string | undefined = useRecordContext()?.userIdCNHS;
  return (
    <Box display="inline-flex" alignItems="center" gap={0.5}>
      <CopyableValue source="userIdCNHS" />
      {userId && (
        <Button size="small" href={`${COUNTER_URL}/coinhouse-counter/${userId}`} target="_blank" startIcon={<OpenInNewRoundedIcon />}>
          {t("Coinhouse Counter")}
        </Button>
      )}
    </Box>
  );
};

// WCAG AA for body text. A colour the picker cannot parse yet counts as readable.
const readable = (foreground?: string, background?: string) => {
  if (!foreground || !background) return true;
  try {
    return getContrastRatio(foreground, background) >= 4.5;
  } catch {
    return true;
  }
};

type PaymentPageLook = { name?: string; description?: string; logo?: string; backgroundColor?: string; fontColor?: string };

// The top of the payment page as customers see it: the logo shown on the brand colour
// (logoInverse), the name and the description, in the font colour on the background
// colour. `action` sits under it.
const PaymentPagePreview = ({ name, description, logo, backgroundColor, fontColor, action }: PaymentPageLook & { action?: ReactNode }) => {
  const { t }: { t: any } = useTranslation();
  return (
    <Box gridColumn="1 / -1" width="100%" maxWidth={560}>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        px={2.5}
        py={1.5}
        sx={{ borderRadius: "12px", border: 1, borderColor: "divider", backgroundColor: backgroundColor || "background.default", color: fontColor || "text.primary" }}
      >
        {logo && <img src={logo} alt={t("Logo preview")} style={{ maxHeight: 24, maxWidth: 110 }} />}
        <Box minWidth={0}>
          <Typography variant="subtitle2" noWrap sx={{ color: "inherit" }}>
            {name || t("Entity name")}
          </Typography>
          {description && (
            <Typography variant="body2" noWrap sx={{ color: "inherit", opacity: 0.8 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>
      {!readable(fontColor, backgroundColor) && (
        <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: 13 }}>
          {t("Customers will struggle to read the text: pick a font color that stands out from the background color.")}
        </Alert>
      )}
      {action && <Box mt={1.5}>{action}</Box>}
    </Box>
  );
};

// The preview as the form currently sets it. A logo only shows once it is saved.
export const LivePaymentPagePreview = () => {
  const draft = useWatch({ name: "parameters.physicalPayment" }) ?? {};
  const logo = useRecordContext()?.parameters?.physicalPayment?.logoInverse;
  return <PaymentPagePreview {...draft} logo={logo} action={<PaymentPagePreviewButton draft={draft} />} />;
};

// The preview as the entity is saved.
export const SavedPaymentPagePreview = () => {
  const { logoInverse, ...look } = useRecordContext()?.parameters?.physicalPayment ?? {};
  return <PaymentPagePreview {...look} logo={logoInverse} action={<PaymentPagePreviewButton />} />;
};
